/**
 * Portal-level preferences that are not owned by one feature's API module.
 */

import { useQuery } from '@tanstack/react-query';
import { request } from './api';

export interface LeadFollowUpSettings {
  /**
   * Follow-up offsets in days, in the order they are offered.
   *
   * The wire and storage names still say "snooze" — the Worker route and the
   * stored `snooze_duration_days` key predate the rename, and rewriting a
   * persisted settings row to change a word nobody sees would be a migration
   * for vocabulary's sake. Everything a user reads says Follow Up.
   */
  snoozeDurationDays: number[];
  persisted: boolean;
}

/** Matches the Worker's fallback, so the control works before anything is saved. */
export const DEFAULT_FOLLOW_UP_OFFSET_DAYS = [1, 3, 7];
export const FOLLOW_UP_OPTION_COUNT = 3;

export const portalSettingsApi = {
  leadFollowUp: () =>
    request<{ settings: LeadFollowUpSettings }>('/api/settings/lead-follow-up'),
  saveLeadFollowUp: (offsetDays: number[]) =>
    request<{ settings: LeadFollowUpSettings }>('/api/settings/lead-follow-up', {
      method: 'PUT',
      body: JSON.stringify({ snoozeDurationDays: offsetDays }),
    }),
};

export const LEAD_FOLLOW_UP_SETTINGS_KEY = ['settings', 'lead-follow-up'];

/**
 * The configured Follow Up offsets. Deliberately never in an error state: if the
 * preference cannot be read, the Action Center still offers the defaults rather
 * than losing the control.
 */
export function useFollowUpOffsets(): number[] {
  const query = useQuery({
    queryKey: LEAD_FOLLOW_UP_SETTINGS_KEY,
    queryFn: portalSettingsApi.leadFollowUp,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const offsets = query.data?.settings?.snoozeDurationDays;
  return offsets?.length ? offsets : DEFAULT_FOLLOW_UP_OFFSET_DAYS;
}
